import { useTranslation } from 'react-i18next';
import { Alert, Grid, Paper, Stack, Typography } from '@mui/material';
import { Menu } from '../types';

interface OverviewTabProps {
  menu: Menu;
}

const SummaryBlock = ({ label, value }: { label: string; value: string }) => (
  <Stack spacing={0.5}>
    <Typography variant="textXS"
      color="text.secondary">{label}</Typography>
    <Typography variant="textMD"
      fontWeight={600}>{value}</Typography>
  </Stack>
);

/** Read-only summary aggregating the headline fields from every other tab, so users don't
 * need to click through all nine tabs just to get an at-a-glance view. */
const OverviewTab = ({ menu }: OverviewTabProps) => {
  const { t } = useTranslation('menus');
  const dishCount = menu.courses.reduce((sum, c) => sum + c.dishes.length, 0);

  return (
    <Stack spacing={4}>
      {menu.trial && (
        <Alert severity="info"
          icon={false}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 1 }}>
            {t('trial.title', 'Trial Programme')}
          </Typography>
          <Grid container
            spacing={4}>
            <Grid item
              xs={6}
              sm={3}>
              <SummaryBlock label={t('trial.siteCount', 'Trial sites')}
                value={String(menu.trial.siteCount)} />
            </Grid>
            <Grid item
              xs={6}
              sm={3}>
              <SummaryBlock label={t('trial.startDate', 'Start date')}
                value={menu.trial.startDate} />
            </Grid>
            <Grid item
              xs={6}
              sm={3}>
              <SummaryBlock label={t('trial.endDate', 'End date')}
                value={menu.trial.endDate} />
            </Grid>
            <Grid item
              xs={6}
              sm={3}>
              <SummaryBlock label={t('trial.reviewDate', 'Review date')}
                value={menu.trial.reviewDate} />
            </Grid>
          </Grid>
        </Alert>
      )}

      <Paper variant="outlined"
        sx={{ p: 4 }}>
        <Grid container
          spacing={5}>
          <Grid item
            md={3}
            sm={6}
            xs={12}>
            <SummaryBlock label={t('menuTypeLabel', 'Menu Type')}
              value={t(`menuType.${menu.menuType}`, menu.menuType)} />
          </Grid>
          <Grid item
            md={3}
            sm={6}
            xs={12}>
            <SummaryBlock label={t('menuCycleLabel', 'Menu Cycle')}
              value={t(`menuCycle.${menu.menuCycle}`, menu.menuCycle)} />
          </Grid>
          <Grid item
            md={3}
            sm={6}
            xs={12}>
            <SummaryBlock label={t('servicePeriods', 'Service Periods')}
              value={menu.servicePeriods.map((p) => p.name).join(', ') || '-'} />
          </Grid>
          <Grid item
            md={3}
            sm={6}
            xs={12}>
            <SummaryBlock label={t('courses', 'Courses')}
              value={String(menu.courses.length)} />
          </Grid>
          <Grid item
            md={3}
            sm={6}
            xs={12}>
            <SummaryBlock label={t('dishes', 'Dishes')}
              value={String(dishCount)} />
          </Grid>
          <Grid item
            md={3}
            sm={6}
            xs={12}>
            <SummaryBlock label={t('sites', 'Sites')}
              value={String(menu.sites.length)} />
          </Grid>
          <Grid item
            md={3}
            sm={6}
            xs={12}>
            <SummaryBlock label={t('targetMargin', 'Target Margin')}
              value={`${menu.targetMarginPercent}%`} />
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );
};

export default OverviewTab;
